import React from "react";
import { Label, FormGroup, Row, Input, UncontrolledTooltip } from "reactstrap";
import { Info } from "react-feather";
import DatePicker from "react-datepicker";
import { SelectFormField, InputFormField } from "./OfferFormFields";

const BasicInfoTab = ({
  fields,
  errors,
  options,
  hasUsageLimit,
  setHasUsageLimit,
  trigger,
  dateHelpers,
}) => {
  const {
    nameField,
    offerCodeField,
    serviceCityIdField,
    carTypeField,
    rewardTypeField,
    rewardDiscountApplyOnField,
    rewardAmountField,
    validFromField,
    validUptoField,
    usageLimitField,
  } = fields;

  const {
    cityOptions,
    carTypeOptions,
    rewardTypeOptions,
    discountApplyOnOptions,
  } = options;

  const { getMinTimeForValidUpto, getMaxTimeForValidUpto } = dateHelpers;

  return (
    <Row>
      <InputFormField
        label="Offer Name"
        field={nameField}
        fieldName="name"
        errors={errors}
        type="text"
        maxLength="100"
      />
      <InputFormField
        label="Offer Code"
        field={offerCodeField}
        fieldName="offerCode"
        errors={errors}
        type="text"
        placeholder="Enter offer code"
        maxLength="25"
        onChange={(e) => {
          const val = e.target.value.replace(/\s/g, "");
          offerCodeField.onChange(val);
        }}
      />
      <SelectFormField
        label="Service City"
        field={serviceCityIdField}
        fieldName="serviceCityId"
        errors={errors}
        colSize="3"
        options={cityOptions}
        placeholder="Select City"
        onChange={(val) => {
          serviceCityIdField.onChange(val);
          carTypeField.onChange([]);
        }}
      />
      <SelectFormField
        label="Car Type"
        field={carTypeField}
        fieldName="carType"
        errors={errors}
        colSize="3"
        isMulti
        options={carTypeOptions}
        isDisabled={!serviceCityIdField.value}
        placeholder={
          serviceCityIdField.value ? "Select Car Type" : "Select City first"
        }
      />
      <SelectFormField
        label="Reward Type"
        field={rewardTypeField}
        fieldName="rewardType"
        errors={errors}
        colSize="3"
        options={rewardTypeOptions}
        placeholder="Select Reward Type"
        onChange={(val) => {
          rewardTypeField.onChange(val);
          trigger("rewardAmount");
        }}
      />
      <SelectFormField
        label="Discount Apply On"
        field={rewardDiscountApplyOnField}
        fieldName="rewardDiscountApplyOn"
        errors={errors}
        colSize="3"
        options={discountApplyOnOptions}
        placeholder="Select Discount Type"
        onChange={(val) => {
          rewardDiscountApplyOnField.onChange(val);
          trigger("rewardAmount");
        }}
      />
      <InputFormField
        label="Reward Amount"
        field={rewardAmountField}
        fieldName="rewardAmount"
        errors={errors}
        type="number"
        min="0"
        step="0.01"
        onKeyDown={(e) => {
          if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
        }}
        onChange={(e) => {
          const val = e.target.value;
          if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
            rewardAmountField.onChange(val);
          }
        }}
      />



      <FormGroup className="col-lg-3">
        <Label>
          Valid From <span className="text-danger">*</span>
        </Label>
        <div className="date-picker-container">
          <DatePicker
            className={`form-control datetimepicker-input digits ${errors.validFrom ? "is-invalid" : ""}`}
            selected={validFromField.value}
            onChange={validFromField.onChange}
            timeInputLabel="Time:"
            showTimeInput
            dateFormat="MMMM d, yyyy h:mm aa"
            placeholderText="Select Date & Time"
            minDate={dateHelpers.getMinDateForValidFrom?.()}
            minTime={dateHelpers.getMinTimeForValidFrom?.()}
            maxTime={dateHelpers.getMaxTimeForValidUpto?.()}
          />
        </div>
        {errors.validFrom && (
          <span className="text-danger">{errors.validFrom.message}</span>
        )}
      </FormGroup>

      <FormGroup className="col-lg-3">
        <Label>
          Valid Upto <span className="text-danger">*</span>
        </Label>
        <div className="date-picker-container">
          <DatePicker
            className={`form-control datetimepicker-input digits ${errors.validUpto ? "is-invalid" : ""}`}
            selected={validUptoField.value}
            onChange={validUptoField.onChange}
            timeInputLabel="Time:"
            showTimeInput
            dateFormat="MMMM d, yyyy h:mm aa"
            placeholderText="Select Date & Time"
            minDate={validFromField.value}
            minTime={getMinTimeForValidUpto()}
            maxTime={getMaxTimeForValidUpto()}
          />
        </div>
        {errors.validUpto && (
          <span className="text-danger">{errors.validUpto.message}</span>
        )}
      </FormGroup>

      <FormGroup
        className="col-lg-3 d-flex align-items-center"
        style={{ paddingTop: "30px" }}
      >
        <div className="d-flex align-items-center gap-1">
          <div className="checkbox checkbox-primary" style={{ margin: 0 }}>
            <Input
              type="checkbox"
              id="hasUsageLimitCheckbox"
              checked={hasUsageLimit}
              onChange={(e) => {
                setHasUsageLimit(e.target.checked);
                if (!e.target.checked) usageLimitField.onChange("");
              }}
            />
            <Label
              htmlFor="hasUsageLimitCheckbox"
              className="mb-0"
              style={{ cursor: "pointer" }}
            >
              Usage Limit
            </Label>
          </div>
          <span
            id="usageLimitInfoIcon"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              padding: "4px",
            }}
          >
            <Info size={16} color="#6c757d" />
          </span>
          <UncontrolledTooltip
            placement="right-start"
            target="usageLimitInfoIcon"
            container="body"
          >
            Usage limit applies per customer.
            <br />
            If set to 10, each customer can use the offer 10 times.
            <br />
            If not set, the offer can be used unlimited times until it expires.
          </UncontrolledTooltip>
        </div>
      </FormGroup>

      {hasUsageLimit && (
        <InputFormField
          label="Usage Limit"
          field={usageLimitField}
          fieldName="usageLimit"
          errors={errors}
          type="number"
          min="1"
          maxLength="5"
          onKeyDown={(e) => {
            if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault();
          }}
          onInput={(e) => {
            if (e.target.value.length > 5) {
              e.target.value = e.target.value.slice(0, 5);
              usageLimitField.onChange(e.target.value);
            }
          }}
          placeholder="Enter usage limit"
        />
      )}
    </Row>
  );
};

export default BasicInfoTab;
